import { Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, map, of, switchMap } from 'rxjs';
import { MaintenanceJobState } from 'src/app/maintenance-user/state/maintenance-job.state';
import { CorrectiveActionState } from 'src/app/maintenance-user/state/corrective-action.state';
import { ServiceReportState } from 'src/app/maintenance-user/state/service-report.state';
import { TaskState } from 'src/app/task-user/state/task.state';
import { DrillsState } from 'src/app/drills-user/state/drills.state';
import { RunTimeState } from 'src/app/run-time/state/run-time.state';
import { ProductState } from 'src/app/storage-user/state/product.state';
import { MedicineState } from 'src/app/medicine-user/state/medicine.state';
import { OrderState } from 'src/app/order-user/state/order.state';
import { DepartmentState } from 'src/app/department/state/department.state';
import { DepartmentGroupState } from 'src/app/department/state/department-group.state';
import { UserState } from 'src/app/shared/user-view/state/user.state';
import { fullName } from 'src/app/shared/functions/full-name';
import { PaxState } from 'src/app/user/state/pax.state';
import { CertificateState } from 'src/app/certificate-user/state/certificate.state';
import { DocumentState } from 'src/app/document-user/state/document.state';
import { RiskAssessmentState } from 'src/app/risk-assessment-user/state/risk-assessment.state';
import { NonConformanceState } from 'src/app/non-conformance-user/state/non-conformance.state';
import { FormsLibraryState } from 'src/app/custom-forms/state/forms-library.state';
import { AlertState } from 'src/app/alert/state/alert.state';
import { QuantityTypeState } from 'src/app/quantity-type/state/quantity-type.state';
import { ShiftState } from 'src/app/crew-management/state/shifts/shift.state';
import { ActivityState } from 'src/app/crew-management/state/activities/activity.state';
import { MedicineCategoryState } from 'src/app/medicine-admin/state/medicine-category.state';
import { ProductCategoryState } from 'src/app/storage-admin/state/product-category.state';
import { ProductTypeState } from 'src/app/storage-admin/state/product-type.state';
import { CertificateTypeState } from 'src/app/certificate-admin/state/certificate-types.state';
import { OrderTypeState } from 'src/app/order-admin/state/order-types/order-type.state';
import { AccountState } from 'src/app/order-admin/state/account.state';
import { AccountGroupState } from 'src/app/order-admin/state/account-group.state';
import { DeliveryConditionState } from 'src/app/order-admin/state/delivery-condition.state';
import { SupplierState } from 'src/app/order-admin/state/supplier/supplier.state';
import { PurchaserState } from 'src/app/order-admin/state/purchaser/purchaser.state';
import { DeliveryAddressState } from 'src/app/order-admin/state/delivery-address/delivery-address.state';
import { NonConformanceTypesState } from 'src/app/non-conformance-admin/state/types/non-conformance-type.state';
import { SeverityState } from 'src/app/non-conformance-admin/state/severities/severity.state';
import { NonConformanceCauseState } from 'src/app/non-conformance-admin/state/cause/non-conformance-cause.state';
import { NonConformanceSeaStateState } from 'src/app/non-conformance-admin/state/sea-state/non-conformance-sea-state.state';
import { NonConformanceWeatherInformationState } from 'src/app/non-conformance-admin/state/weather-information/non-conformance-weather-information.state';
import { NonConformanceLocationOnDepartmentState } from 'src/app/non-conformance-admin/state/location-on-department/non-conformance-location-on-department.state';
import { CompetenceState } from 'src/app/competence/state/competence.state';
import { CompetenceGroupState } from 'src/app/competence/state/competence-group.state';
import { CompetenceRequirementState } from 'src/app/competence/state/competence-requirement.state';
import { JobPositionState } from 'src/app/competence/state/job-position.state';
import { JobPositionGroupState } from 'src/app/competence/state/job-position-group.state';
import { CompetenceRolesState } from 'src/app/competence/state/competence-roles.state';
import { PrivilegeTemplateState } from 'src/app/competence/state/privilege-template.state';
import { CompetenceUserState } from 'src/app/shared/user-view/state/competence-user.state';
import { TenantState } from 'src/app/tenant/state/tenant.state';
import { ProductOwnerState } from 'src/app/product-owner/state/product-owner.state';
import { TripState } from 'src/app/crew-management-user/state/trips/trip.state';
import { JobTypeState } from 'src/app/maintenance-admin/state/job-type.state';
import { StandardJobTemplateState } from 'src/app/maintenance-admin/state/standard-job-template.state';
import { ComponentsStructureState } from 'src/app/maintenance-admin/state/components-structure.state';
import { JobAreaState } from 'src/app/maintenance-admin/state/job-area.state';
import { WorkScheduleTemplateState } from 'src/app/work-and-rest-admin/state/work-schedule-template/work-schedule-template.state';
import { OvertimeTypeState } from 'src/app/work-and-rest-admin/state/overtime-type/overtime-type.state';
import { WorkPlanTemplateState } from 'src/app/work-and-rest-admin/state/work-plan-template/work-plan-template.state';
import { WorkOverviewState } from 'src/app/work-and-rest-user/state/work-overview.state';
import { SignOffState } from 'src/app/work-and-rest-user/state/sign-off.state';
import { CustomPropertyState } from 'src/app/non-conformance-admin/state/custom-property/custom-property.state';
import { ProjectState } from 'src/app/maintenance-user/state/project.state';
import { SfiCodeState } from 'src/app/order-admin/state/sfi-code.state';
import { ORDER_STATUS_LABELS } from 'src/app/order-user/enums/order-status.enum';
import { MenuTreeStructureState } from '../state/menu-tree-structure.state';
import { CurrentRouteConfigService } from '../services/current-route-config.service';
import { BreadcrumbParams } from '../enums/breadcrumb-params.enum';
import { BreadcrumbInfo } from '../models/breadcrumb-info.model';

@Pipe({ name: 'breadcrumbParse', standalone: true })
export class BreadcrumbParsePipe implements PipeTransform {
  constructor(
    private store: Store,
    private translate: TranslateService,
    private currentRouteConfigService: CurrentRouteConfigService
  ) {}

  transform(value: string, info?: BreadcrumbInfo): Observable<string> {
    if (!value) {
      return of(value);
    }

    const selectorsMap: { [key: string]: Observable<string> } = {
      [BreadcrumbParams.ActiveMenuName]: this.store
        .select(MenuTreeStructureState.getActiveMenuElementName)
        .pipe(map((name) => name || '')),
      [BreadcrumbParams.MaintenanceJobName]: this.store
        .select(MaintenanceJobState.getCurrentMaintenanceJob)
        .pipe(map((job) => job?.name || '')),
      [BreadcrumbParams.MaintenanceJobNameFromVersion]: this.store
        .select(MaintenanceJobState.getCurrentMaintenanceJobVersion)
        .pipe(map((job) => job?.name || '')),
      [BreadcrumbParams.CorrectiveActionName]: this.store
        .select(CorrectiveActionState.getCorrectiveActionById)
        .pipe(map((ca) => ca?.name || '')),
      [BreadcrumbParams.ServiceReportName]: this.store
        .select(ServiceReportState.getServiceReportById)
        .pipe(map((sr) => sr?.name || '')),
      [BreadcrumbParams.UserDetails]: this.store
        .select(UserState.getUserById)
        .pipe(map((user) => (user ? fullName(user) : ''))),
      [BreadcrumbParams.PaxDetails]: this.store
        .select(PaxState.getPaxById)
        .pipe(map((pax) => (pax ? fullName(pax) : ''))),
      [BreadcrumbParams.CertificateName]: this.store
        .select(CertificateState.getCurrentCertificate)
        .pipe(map((certificate) => certificate?.name || '')),
      [BreadcrumbParams.DocumentName]: this.store
        .select(DocumentState.getCurrentDocument)
        .pipe(map((document) => document?.name || '')),
      [BreadcrumbParams.DocumentStructureName]: this.store
        .select(MenuTreeStructureState.getDetailedMenuStructureElement)
        .pipe(map((menuStructure) => menuStructure?.name || '')),
      [BreadcrumbParams.RiskAssessmentName]: this.store
        .select(RiskAssessmentState.getRiskAssessmentById)
        .pipe(map((riskAssessment) => riskAssessment?.name || '')),
      [BreadcrumbParams.RiskAssessmentVersionName]: this.store
        .select(RiskAssessmentState.getRiskAssessmentVersionById)
        .pipe(map((riskAssessmentVersion) => riskAssessmentVersion?.name || '')),
      [BreadcrumbParams.NonConformanceName]: this.store
        .select(NonConformanceState.getNonConformanceById)
        .pipe(map((nonConformance) => nonConformance?.name || '')),
      [BreadcrumbParams.TaskName]: this.store
        .select(TaskState.getCurrentTask)
        .pipe(map((task) => task?.name || '')),
      [BreadcrumbParams.DrillName]: this.store
        .select(DrillsState.getCurrentDrill)
        .pipe(map((drill) => drill?.name || '')),
      [BreadcrumbParams.RunTimeName]: this.store
        .select(RunTimeState.getCurrentRunTime)
        .pipe(map((runTime) => runTime?.name || '')),
      [BreadcrumbParams.ProductName]: this.store
        .select(ProductState.getProductById)
        .pipe(map((product) => product?.name || '')),
      [BreadcrumbParams.MedicineName]: this.store
        .select(MedicineState.getMedicineById)
        .pipe(map((medicine) => medicine?.name || '')),
      [BreadcrumbParams.OrderName]: this.store
        .select(OrderState.getOrderById)
        .pipe(map((order) => order?.title || '')),
      [BreadcrumbParams.OrderStageLabel]: this.currentRouteConfigService.currentRouteConfig$.pipe(
        switchMap((routeConfig) =>
          this.store
            .select(OrderState.getOrderById)
            .pipe(
              switchMap((order) =>
                this.translate.get(
                  order?.id
                    ? (ORDER_STATUS_LABELS[order.orderStage] ?? 'drafts')
                    : routeConfig?.path === 'new'
                      ? 'draft'
                      : 'loading'
                )
              )
            )
        )
      ),
      [BreadcrumbParams.DepartmentName]: this.store
        .select(DepartmentState.getCurrentDepartment)
        .pipe(map((department) => department?.name || '')),
      [BreadcrumbParams.DepartmentGroupName]: this.store
        .select(DepartmentGroupState.getCurrentDepartmentGroup)
        .pipe(map((departmentGroup) => departmentGroup?.name || '')),
      [BreadcrumbParams.QuantityTypeName]: this.store
        .select(QuantityTypeState.getCurrentQuantityType)
        .pipe(map((quantityType) => quantityType?.name || '')),
      [BreadcrumbParams.AlertName]: this.store
        .select(AlertState.getCurrentAlert)
        .pipe(map((alert) => alert?.name || '')),
      [BreadcrumbParams.ShiftName]: this.store
        .select(ShiftState.getShiftById)
        .pipe(map((shift) => shift?.name || '')),
      [BreadcrumbParams.ActivityName]: this.store
        .select(ActivityState.getActivityById)
        .pipe(map((activity) => activity?.name || '')),
      [BreadcrumbParams.CustomFormName]: this.store
        .select(FormsLibraryState.getCustomFormBasics)
        .pipe(map((customForm) => customForm?.name || '')),
      [BreadcrumbParams.FormLibraryName]: this.store
        .select(FormsLibraryState.getFormsLibraryById)
        .pipe(map((formsLibrary) => formsLibrary?.name || '')),
      [BreadcrumbParams.MedicineCategoryName]: this.store
        .select(MedicineCategoryState.getCurrentMedicineCategory)
        .pipe(map((medicineCategory) => medicineCategory?.name || '')),
      [BreadcrumbParams.ProductCategoryName]: this.store
        .select(ProductCategoryState.getCurrentProductCategory)
        .pipe(map((productCategory) => productCategory?.name || '')),
      [BreadcrumbParams.ProductTypeName]: this.store
        .select(ProductTypeState.getProductTypeById)
        .pipe(map((productType) => productType?.name || '')),
      [BreadcrumbParams.CertificateTypeName]: this.store
        .select(CertificateTypeState.getCurrentCertificateType)
        .pipe(map((certificateType) => certificateType?.name || '')),
      [BreadcrumbParams.OrderTypeName]: this.store
        .select(OrderTypeState.getOrderTypeById)
        .pipe(map((orderType) => orderType?.name || '')),
      [BreadcrumbParams.AccountName]: this.store
        .select(AccountState.getCurrentAccount)
        .pipe(map((account) => account?.name || '')),
      [BreadcrumbParams.AccountGroupName]: this.store
        .select(AccountGroupState.getCurrentAccountGroup)
        .pipe(map((accountGroup) => accountGroup?.name || '')),
      [BreadcrumbParams.DeliveryConditionName]: this.store
        .select(DeliveryConditionState.getCurrentDeliveryCondition)
        .pipe(map((deliveryCondition) => deliveryCondition?.name || '')),
      [BreadcrumbParams.SupplierName]: this.store
        .select(SupplierState.getSupplierById)
        .pipe(map((supplier) => supplier?.name || '')),
      [BreadcrumbParams.PurchaserName]: this.store
        .select(PurchaserState.getCurrentPurchaser)
        .pipe(map((purchaser) => purchaser?.name || '')),
      [BreadcrumbParams.SfiCodeName]: this.store
        .select(SfiCodeState.getCurrentSfiCode)
        .pipe(map((purchaser) => purchaser?.name || '')),
      [BreadcrumbParams.DeliveryAddressName]: this.store
        .select(DeliveryAddressState.getCurrentDeliveryAddress)
        .pipe(map((deliveryAddress) => deliveryAddress?.name || '')),
      [BreadcrumbParams.NonConformanceTypeName]: this.store
        .select(NonConformanceTypesState.getCurrentNonConformanceType)
        .pipe(map((nonConformanceType) => nonConformanceType?.name || '')),
      [BreadcrumbParams.SeverityTypeName]: this.store
        .select(SeverityState.getSelectedSeverity)
        .pipe(map((severity) => severity?.name || '')),
      [BreadcrumbParams.CauseName]: this.store
        .select(NonConformanceCauseState.getCurrentCause)
        .pipe(map((cause) => cause?.name || '')),
      [BreadcrumbParams.SeaStateName]: this.store
        .select(NonConformanceSeaStateState.getCurrentSeaState)
        .pipe(map((seaState) => seaState?.name || '')),
      [BreadcrumbParams.WeatherInformationName]: this.store
        .select(NonConformanceWeatherInformationState.getCurrentWeatherInformation)
        .pipe(map((weatherInformation) => weatherInformation?.name || '')),
      [BreadcrumbParams.LocationOnDepartmentName]: this.store
        .select(NonConformanceLocationOnDepartmentState.getCurrentLocationOnDepartment)
        .pipe(map((locationOnDepartment) => locationOnDepartment?.name || '')),
      [BreadcrumbParams.CompetenceName]: this.store
        .select(CompetenceState.getCurrentCompetence)
        .pipe(map((competence) => competence?.name || '')),
      [BreadcrumbParams.CompetenceGroupName]: this.store
        .select(CompetenceGroupState.getCurrentCompetenceGroup)
        .pipe(map((competenceGroup) => competenceGroup?.name || '')),
      [BreadcrumbParams.CompetenceRequirementName]: this.store
        .select(CompetenceRequirementState.getCurrentCompetenceRequirement)
        .pipe(map((competenceRequirement) => competenceRequirement?.competenceCode || '')),
      [BreadcrumbParams.JobPositionName]: this.store
        .select(JobPositionState.getCurrentJobPosition)
        .pipe(map((jobPosition) => jobPosition?.name || '')),
      [BreadcrumbParams.JobPositionGroupName]: this.store
        .select(JobPositionGroupState.getCurrentJobPositionGroup)
        .pipe(map((jobPositionGroup) => jobPositionGroup?.name || '')),
      [BreadcrumbParams.CompetenceRoleName]: this.store
        .select(CompetenceRolesState.getDetailedRole)
        .pipe(map((competenceRole) => competenceRole?.name || '')),
      [BreadcrumbParams.PrivilegeTemplateName]: this.store
        .select(PrivilegeTemplateState.getCurrentPrivilegeTemplate)
        .pipe(map((privilegeTemplate) => privilegeTemplate?.name || '')),
      [BreadcrumbParams.UserCompetenceName]: this.store
        .select(CompetenceUserState.getCurrentCompetenceUser)
        .pipe(map((userCompetence) => userCompetence?.competenceName || '')),
      [BreadcrumbParams.CompetenceUserName]: this.store
        .select(CompetenceUserState.getCurrentCompetenceUser)
        .pipe(map((userCompetence) => (userCompetence ? fullName(userCompetence as any) : ''))),
      [BreadcrumbParams.TenantName]: this.store
        .select(TenantState.getCurrentTenant)
        .pipe(map((tenant) => tenant?.name || '')),
      [BreadcrumbParams.ProductOwnerName]: this.store
        .select(ProductOwnerState.getCurrentProductOwner)
        .pipe(map((productOwner) => productOwner?.displayName || '')),
      [BreadcrumbParams.TripName]: this.store
        .select(TripState.getTripById)
        .pipe(map((trip) => trip?.name || '')),
      [BreadcrumbParams.JobTypeName]: this.store
        .select(JobTypeState.getCurrentJobType)
        .pipe(map((jobType) => jobType?.name || '')),
      [BreadcrumbParams.StandardJobTemplateName]: this.store
        .select(StandardJobTemplateState.getStandardJobTemplateById)
        .pipe(map((standardJobTemplate) => standardJobTemplate?.name || '')),
      [BreadcrumbParams.ComponentName]: this.store
        .select(ComponentsStructureState.getComponentDetails)
        .pipe(map((component) => component?.name || '')),
      [BreadcrumbParams.JobAreaName]: this.store
        .select(JobAreaState.getCurrentJobArea)
        .pipe(map((jobArea) => jobArea?.name || '')),
      [BreadcrumbParams.ComponentGroupName]: this.store
        .select(ComponentsStructureState.getComponentGroupDetails)
        .pipe(map((componentGroup) => componentGroup?.name || '')),
      [BreadcrumbParams.SubtaskName]: this.store
        .select(NonConformanceState.getCurrentSubtask)
        .pipe(
          map((subtask) => (Array.isArray(subtask) ? subtask[subtask.length - 1]?.name || '' : ''))
        ),
      [BreadcrumbParams.CustomPropertyName]: this.store
        .select(CustomPropertyState.getSelectedCustomProperty)
        .pipe(map((customProperty) => customProperty?.name || '')),
      [BreadcrumbParams.WorkScheduleTemplateName]: this.store
        .select(WorkScheduleTemplateState.getCurrentWorkScheduleTemplate)
        .pipe(map((workScheduleTemplate) => workScheduleTemplate?.templateName || '')),
      [BreadcrumbParams.WorkOverviewName]: this.store
        .select(WorkOverviewState.getWorkOverviewById)
        .pipe(map((workOverview) => workOverview?.user?.name || '')),
      [BreadcrumbParams.OvertimeTypeName]: this.store
        .select(OvertimeTypeState.getCurrentOvertimeType)
        .pipe(map((overtimeType) => overtimeType?.name || '')),
      [BreadcrumbParams.WorkPlanTemplateName]: this.store
        .select(WorkPlanTemplateState.getCurrentWorkPlan)
        .pipe(map((workPlanTemplate) => workPlanTemplate?.name || '')),
      [BreadcrumbParams.SignOffName]: this.store
        .select(SignOffState.getSignOffById)
        .pipe(map((signOff) => signOff?.userFullName || '')),
      [BreadcrumbParams.ProjectName]: this.store
        .select(ProjectState.getSelectedProject)
        .pipe(map((project) => project?.name || '')),
    };

    const replaceParams = Object.keys(info?.params || {});

    if (replaceParams.length === 0) {
      return of(value);
    }

    const neededObservables: Observable<any>[] = Object.keys(info?.params || {}).map(
      (selector) => selectorsMap[info?.params[selector]]
    );
    neededObservables.push(this.currentRouteConfigService.currentRouteConfig$);

    return combineLatest(neededObservables).pipe(
      map((values) => {
        let result = info?.usePath || this.currentRouteConfigService.get()?.path || '';
        replaceParams.forEach((param, index) => {
          result = result.replace(`:${param}`, values[index]);
        });

        return this.addSpacesAroundSlashes(result);
      })
    );
  }

  addSpacesAroundSlashes(text: string): string {
    return text.replace(/([^ ])\/([^ ])/g, '$1 / $2').replace(/([^ ])\/$/g, '$1 /');
  }
}
